import { useState, useEffect, useRef } from 'react';
import { 
  CircleDashed, 
  Check, 
  Coffee, 
  BookText, 
  BookOpenCheck,
  BadgeHelp, 
  Search, 
  Link, 
  ListChecks, 
  PenLine, 
  ThumbsUp,
  CreditCard,
  Brain,
  ArrowRight,
  Play,
  RotateCcw,
  LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLessonWithSlides, LessonSlide, TopicLesson } from '@/hooks/useQueryHooks';
import { useAuth } from '@/contexts/AuthContext';
import { config } from '@/lib/config';
import { useToast } from '@/hooks/use-toast';

// Helper function to get icon for each slide type
const getSlideTypeIcon = (slideType: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    // Learn section
    'intro': Coffee,
    'learn': BookOpenCheck,
    'learn_hands_on': BookText,
    // Practice section
    'practice_faq': BadgeHelp,
    'practice_spot': Search,
    'practice_match': Link,
    // Test section
    'assessment_mcq': ListChecks,
    'assessment_fill': PenLine,
    'assessment_true_false': ThumbsUp,
    // Additional types
    'flashcard': CreditCard,
    'quiz': Brain,
    'next_steps': ArrowRight,
  };
  
  return iconMap[slideType] || BookText; // Default to BookText if type not found
};

interface LessonActivity {
  id: string;
  title: string;
  subtitle: string;
  duration: string;
  completed: boolean;
  imageUrl?: string;
  slideType: string;
  type: 'learn' | 'faq' | 'quiz' | 'matching' | 'flashcard';
  content?: LessonSlide;
}

interface LessonViewProps {
  topicId: string | undefined;
  lessonId: string;
  onActivityClick?: (activity: LessonActivity) => void;
  onSlidesLoaded?: (payload: { lesson: TopicLesson; slides: LessonSlide[] }) => void;
}

const ActivityCard = ({ 
  activity, 
  onClick 
}: { 
  activity: LessonActivity;
  onClick?: () => void;
}) => {
  const IconComponent = getSlideTypeIcon(activity.slideType);
  
  return (
    <div 
      className="flex items-center justify-between bg-white rounded-[15px] border border-[#f0f0f0] px-3 py-2 h-[51px] cursor-pointer hover:bg-[#fafafa] transition-colors"
      onClick={onClick}
    >
      {/* Left Side */}
      <div className="flex items-center gap-[10px]">
        {/* Thumbnail */}
        <div className="w-[34px] h-[35px] rounded-[9px] bg-transparent flex items-center justify-center flex-shrink-0 overflow-hidden relative">
          {activity.imageUrl ? (
            <img src={activity.imageUrl} alt={activity.title} className="w-full h-full object-cover" />
          ) : (
            <IconComponent className="w-6 h-6" style={{ color: '#666666' }} strokeWidth={2} />
          )}
        </div>

        {/* Title and Subtitle */}
        <div className="flex flex-col gap-[2px]">
          <p className="text-[12px] font-normal text-black truncate">
            {activity.title}
          </p>
          <p className="text-[11px] font-light text-black/50">
            {activity.subtitle}
          </p>
        </div>
      </div>

      {/* Right Side */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-[11px] font-light text-black/50">
          {activity.duration}
        </span>
        {activity.completed ? (
          <div className="w-[24px] h-[23px] rounded-full bg-black flex items-center justify-center">
            <Check className="w-3 h-3 text-white" strokeWidth={2} />
          </div>
        ) : (
          <CircleDashed className="w-[24px] h-[23px] text-black" strokeWidth={1.67} />
        )}
      </div>
    </div>
  );
};

const LessonView = ({
  topicId,
  lessonId,
  onActivityClick,
  onSlidesLoaded,
}: LessonViewProps) => {
  const { session } = useAuth();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const { data: lessonData, isLoading, refetch } = useLessonWithSlides(topicId, lessonId);
  const hasTriggeredGenerationRef = useRef(false);
  const hasAnimatedRef = useRef<string | undefined>(undefined);
  
  // Track lesson ID changes to trigger animation only on lesson switch
  useEffect(() => {
    if (lessonId && lessonId !== hasAnimatedRef.current) {
      setShouldAnimate(true);
      hasAnimatedRef.current = lessonId;
    } else {
      setShouldAnimate(false);
    }
  }, [lessonId]);
  
  // Notify parent when slides are loaded
  useEffect(() => {
    if (lessonData?.slides && onSlidesLoaded) {
      onSlidesLoaded({ lesson: lessonData.lesson, slides: lessonData.slides });
    }
  }, [lessonData?.lesson, lessonData?.slides, onSlidesLoaded]);

  // Trigger generation if lesson is pending (only once per lesson mount)
  useEffect(() => {
    // Early return if already triggered
    if (hasTriggeredGenerationRef.current) return;
    if (!lessonData || !topicId || !session) return;
    
    const { lesson } = lessonData;
    
    // Only trigger if status is 'pending'
    if (lesson.status === 'pending') {
      hasTriggeredGenerationRef.current = true;
      setIsGenerating(true);
      
      const triggerGeneration = async () => {
        try {
          console.log('🔄 Triggering lesson generation for:', lesson.title);
          
          const response = await fetch(
            `${config.BACKEND_API_URL}/api/topics/${topicId}/lessons/${lessonId}/generate`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              }
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate lesson');
          }

          const result = await response.json();
          console.log('✅ Lesson generated:', result);
          
          // Refetch to get updated lesson and slides
          await refetch();
          
        } catch (error) {
          console.error('Failed to generate lesson:', error);
          hasTriggeredGenerationRef.current = false; // Reset on error to allow retry
          toast({
            title: "Generation failed",
            description: error instanceof Error ? error.message : 'Please try again',
            variant: "destructive"
          });
        } finally {
          setIsGenerating(false);
        }
      };
      
      triggerGeneration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonData?.lesson?.status, lessonId]); // Minimal dependencies
  
  // Reset generation flag when switching lessons
  useEffect(() => {
    hasTriggeredGenerationRef.current = false;
  }, [lessonId]);

  // Loading state
  if (isLoading || isGenerating) {
    return (
      <div className="h-full flex items-center justify-center bg-[#f8f8f7]">
        <div className="text-center">
          <p className="text-sm text-[#7a7a7a] mb-2">
            {isGenerating ? 'Generating lesson...' : 'Loading lesson...'}
          </p>
        </div>
      </div>
    );
  }

  if (!lessonData) {
    return (
      <div className="h-full flex items-center justify-center bg-[#f8f8f7]">
        <div className="text-center">
          <p className="text-sm text-[#7a7a7a]">
            Lesson not found
          </p>
        </div>
      </div>
    );
  }

  const { lesson, slides } = lessonData;

  // Transform slides into activities grouped by section
  const learnActivities: LessonActivity[] = slides
    .filter(slide => slide.section === 'learn')
    .map(slide => ({
      id: slide.id,
      title: slide.title,
      subtitle: slide.slide_type === 'intro' ? 'Intro' : slide.slide_type.replace('learn_', '').replace('_', ' '),
      duration: `${slide.duration_minutes || 5} minutes`,
      completed: slide.completed,
      slideType: slide.slide_type,
      type: 'learn' as const,
      content: slide
    }));

  const practiceActivities: LessonActivity[] = slides
    .filter(slide => slide.section === 'practice')
    .map(slide => ({
      id: slide.id,
      title: slide.title,
      subtitle: slide.slide_type.replace('practice_', '').toUpperCase(),
      duration: `${slide.duration_minutes || 5} minutes`,
      completed: slide.completed,
      slideType: slide.slide_type,
      type: slide.slide_type.includes('faq') ? 'faq' as const : slide.slide_type.includes('match') ? 'matching' as const : 'quiz' as const,
      content: slide
    }));

  const testActivities: LessonActivity[] = slides
    .filter(slide => slide.section === 'test')
    .map(slide => ({
      id: slide.id,
      title: slide.title,
      subtitle: slide.slide_type.replace('assessment_', '').toUpperCase(),
      duration: `${slide.duration_minutes || 5} minutes`,
      completed: slide.completed,
      slideType: slide.slide_type,
      type: 'quiz' as const,
      content: slide
    }));

  // Combine all activities in order
  const allActivities = [...learnActivities, ...practiceActivities, ...testActivities];
  
  // Check if any slides are completed
  const hasCompletedSlides = allActivities.some(activity => activity.completed);
  
  // Find first incomplete slide (for Resume)
  const firstIncompleteActivity = allActivities.find(activity => !activity.completed);
  
  // First slide overall (for Start over / Start learning)
  const firstActivity = allActivities[0];

  const handleResumeClick = () => {
    if (firstIncompleteActivity && onActivityClick) {
      onActivityClick(firstIncompleteActivity);
    }
  };

  const handleStartOverClick = () => {
    if (firstActivity && onActivityClick) {
      onActivityClick(firstActivity);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#f8f8f7]">
      {/* Scrollable Content */}
      <ScrollArea className="flex-1">
        <div className="px-[6%] py-8">
          <div className="max-w-3xl mx-auto">
            {/* Lesson Number Label with Progress and Action Buttons */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#7a7a7a] text-sm">
                    Lesson {lesson.lesson_number}
                  </p>
                  <p className="text-[#7a7a7a] text-sm">
                    {lesson.completion_percentage}% Complete
                  </p>
                </div>
                <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#27c840] transition-all duration-500"
                    style={{ width: `${lesson.completion_percentage}%` }}
                  />
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="ml-6 flex items-center gap-2 flex-shrink-0">
                {hasCompletedSlides ? (
                  <>
                    <Button 
                      size="icon"
                      className="bg-[#27c840] hover:bg-[#20a535] text-white rounded-lg h-9 w-9"
                      onClick={handleResumeClick}
                      disabled={!firstIncompleteActivity}
                      aria-label="Resume Learning"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="icon"
                      variant="outline"
                      className="border-[#d0d0d0] text-black hover:bg-[#f5f5f5] rounded-lg h-9 w-9"
                      onClick={handleStartOverClick}
                      aria-label="Start over"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <Button 
                    size="icon"
                    className="bg-[#27c840] hover:bg-[#20a535] text-white rounded-lg h-9 w-9"
                    onClick={handleStartOverClick}
                    disabled={!firstActivity}
                    aria-label="Start Learning"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

            {/* Header Section */}
            <div className="flex items-start gap-9 mb-12">
              {/* Left Side - Text Content */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl font-medium text-foreground mb-2">
                  {lesson.title}
                </h1>
                {/* <p className="text-base italic text-muted-foreground mb-4">
                  {lesson.learning_objectives?.[0] || 'Learn key concepts'}
                </p> */}
                <p className="text-[13px] text-muted-foreground leading-relaxed">
                  {lesson.description || 'Explore the concepts in this lesson'}
                </p>
              </div>

              {/* Right Side - Image */}
              {lesson.lesson_image_url && (
                <div className="w-[185px] h-[185px] flex-shrink-0 rounded-[15px] overflow-hidden border border-[#f0f0f0]">
                  <img src={lesson.lesson_image_url} alt={lesson.title} className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            {/* Content Sections */}
            <div className="space-y-8">
              {/* Learn Section */}
              {learnActivities.length > 0 && (
                <div>
                  <h2 className="text-lg italic text-muted-foreground font-heading font-semibold mb-2">
                    Learn
                  </h2>
                  <div className="space-y-2">
                    {learnActivities.map(activity => (
                      <ActivityCard 
                        key={activity.id} 
                        activity={activity}
                        onClick={() => onActivityClick?.(activity)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Practice Section */}
              {practiceActivities.length > 0 && (
                <div>
                  <h2 className="text-lg italic text-muted-foreground font-heading font-semibold mb-2">
                    Practice
                  </h2>
                  <div className="space-y-2">
                    {practiceActivities.map(activity => (
                      <ActivityCard 
                        key={activity.id} 
                        activity={activity}
                        onClick={() => onActivityClick?.(activity)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Test your knowledge Section */}
              {testActivities.length > 0 && (
                <div>
                  <h2 className="text-lg italic text-muted-foreground font-heading font-semibold mb-2">
                    Test your knowledge
                  </h2>
                  <div className="space-y-2">
                    {testActivities.map(activity => (
                      <ActivityCard 
                        key={activity.id} 
                        activity={activity}
                        onClick={() => onActivityClick?.(activity)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default LessonView;
